import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/i18n";
import App from "@/App";

// ---- Sentry error monitoring (activates only when the DSN env is set at build time) ----
import * as Sentry from "@sentry/react";
import { SENTRY_RELEASE } from "@/appVersion";
import { isFramedPreview } from "./utils/phonePreview";
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,   // real version - never a literal, see appVersion.js
    environment: process.env.NODE_ENV || "production",
    tracesSampleRate: 0, // errors only
    ignoreErrors: [
      "ResizeObserver loop", "Network Error", "Failed to fetch",
      "Load failed", "AbortError",
    ],
  });
}


// Prevent pinch-to-zoom on iOS
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
document.addEventListener('gestureend', (e) => e.preventDefault());

// Hide the iOS keyboard accessory bar (the up/down/✓ toolbar above the
// keyboard). It's a native WebView feature, so this only applies in the
// Capacitor app - not the Safari preview. Guarded so it's a no-op on web or
// if the Keyboard plugin isn't installed.
(async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor?.isNativePlatform?.()) {
      const { Keyboard } = await import('@capacitor/keyboard');
      if (Keyboard?.setAccessoryBarVisible) {
        await Keyboard.setAccessoryBarVisible({ isVisible: false });
      }
    }
  } catch (e) {
    // Plugin not installed or running on web - safe to ignore.
  }
})();

// Prevent double-tap zoom
let lastTouchEnd = 0;
document.addEventListener('touchend', (e) => {
  const now = Date.now();
  if (now - lastTouchEnd <= 300) {
    e.preventDefault();
  }
  lastTouchEnd = now;
}, { passive: false });

// Register Service Worker for PWA and Push Notifications
// Not inside the computer's phone preview frame (V19.19): the copy in the
// frame is a guest of the page around it.
if ('serviceWorker' in navigator && !isFramedPreview()) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered:', registration.scope);
      })
      .catch(err => {
        console.log('SW registration failed:', err);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
